import {
  Button,
  ButtonVariant,
  ColorClass,
  FormMessage,
  FormMessageVariant,
  Icon,
  IconIdentifier,
  Spinner,
} from '@aus-platform/design-system';
import { isNil, isUndefined } from 'lodash';
import { selectHRA, setHRATable } from 'map-3d/shared/map-3d-slices';
import { useEffect } from 'react';
import { handleResponseErrorMessage, useHaulRoad } from 'shared/api';
import { OverflowTextTooltip } from 'shared/components';
import { useAppDispatch, useAppSelector } from 'src/app/hooks';
import { HaulRoadDetailsLayers } from './haul-road-details-layers';

type HaulRoadDetailsProps = {
  haulRoadId: string;
  onClickBackButton: VoidFunction;
};

export const HaulRoadDetails: React.FC<HaulRoadDetailsProps> = ({
  haulRoadId,
  onClickBackButton,
}) => {
  // Selectors.
  const { table } = useAppSelector(selectHRA);

  // Dispatch.
  const dispatch = useAppDispatch();

  // API.
  const {
    data: haulRoadResponseData,
    error: haulRoadErrorResponse,
    isError: isErrorHaulRoad,
    isLoading: isLoadingHaulRoad,
  } = useHaulRoad(haulRoadId);

  // useEffects.
  useEffect(() => {
    handleResponseErrorMessage(isErrorHaulRoad, haulRoadErrorResponse);
  }, [isErrorHaulRoad, haulRoadErrorResponse]);

  // Handlers.
  const onClickViewTableHandler = () => {
    const haulRoadName = haulRoadResponseData?.name;

    dispatch(setHRATable({ ...table, show: true, haulRoadId, haulRoadName }));
  };

  return (
    <>
      {isLoadingHaulRoad ? (
        <Spinner />
      ) : (
        <div className="haul-road-details">
          <div className="haul-road-details-header">
            <Icon
              identifier={IconIdentifier.ArrowLeft}
              colorClass={ColorClass.Neutral300}
              className="haul-road-details-header__back-button"
              cursor
              onClick={onClickBackButton}
            />
            {haulRoadResponseData && (
              <OverflowTextTooltip
                text={haulRoadResponseData.name}
                maxLength={36}
              />
            )}
          </div>
          <div className="haul-road-details-info">
            <div className="haul-road-details-info__title haul-road-details-title">
              <span>Haul Road Details</span>
              <hr />
            </div>
            {!isNil(haulRoadResponseData) && (
              <div className="haul-road-details-info__grid">
                <div className="haul-road-details-info__field">
                  <div className="haul-road-details-info__label">
                    Haul Road Type
                  </div>
                  <div className="haul-road-details-info__value">
                    {haulRoadResponseData.type}
                  </div>
                </div>
                <div className="haul-road-details-info__field">
                  <div className="haul-road-details-info__label">
                    Vehicle Width
                  </div>
                  <div className="haul-road-details-info__value">
                    {haulRoadResponseData.vehicleWidth}m
                  </div>
                </div>
                <div className="haul-road-details-info__field">
                  <div className="haul-road-details-info__label">
                    Chainage Interval
                  </div>
                  <div className="haul-road-details-info__value">
                    {Math.trunc(haulRoadResponseData.chainageInterval)}m
                  </div>
                </div>
                <div className="haul-road-details-info__field">
                  <div className="haul-road-details-info__label">
                    Vehicle Tyre Height
                  </div>
                  <div className="haul-road-details-info__value">
                    {isNil(haulRoadResponseData.vehicleTyreHeight)
                      ? '-'
                      : `${haulRoadResponseData.vehicleTyreHeight}m`}
                  </div>
                </div>
              </div>
            )}
          </div>

          {!isUndefined(haulRoadResponseData) && (
            <HaulRoadDetailsLayers
              haulRoadLayers={haulRoadResponseData?.haulRoadLayers}
              hasBermSupport={
                !isNil(haulRoadResponseData.vehicleTyreHeight)
              }
            />
          )}

          <Button
            className="haul-road-details-view-table-btn"
            onClick={onClickViewTableHandler}
            variant={ButtonVariant.Outline}
            leftIconIdentifier={IconIdentifier.TableRow}
            iconSize={18}
            disabled={!haulRoadResponseData?.isAnalyticsGenerated}
          >
            View Table
          </Button>
        </div>
      )}

      {!isUndefined(haulRoadResponseData) &&
        isNil(haulRoadResponseData.vehicleTyreHeight) && (
          <FormMessage
            variant={FormMessageVariant.Info}
            className="haul-road-details__berm-message"
            message="This haul road analysis was generated before Berm Analysis was introduced. Regenerate to enable Berm Analysis."
          />
        )}
    </>
  );
};
